import React from "react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { useBrand } from "@/lib/brand";
import EstateLegal from "@/pages/sites/EstateLegal";
import { isEstate, isPreviewing } from "@/lib/sitePreview";

/**
 * AgencyLegal — tenant-branded terms / privacy / cookies pages.
 *
 * Content lives in the tenant's brand blob at `brand.legal.<kind>`
 * (title, updated, sections: [{heading, paragraphs}]) so each agency's
 * real legal copy is data, not code — no tenant's terms get baked into
 * a shared component.
 */
function AgencyLegalClassic({ kind }) {
  const brand = useBrand();
  const biz = brand.business || {};
  const doc = brand.legal?.[kind];

  if (!doc) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <main className="flex-1 mx-auto max-w-3xl px-6 lg:px-10 py-24">
          <p className="text-muted-foreground">This page hasn't been published yet.</p>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={`${doc.title}, ${biz.name || "our agency"}`}
        description={doc.sections?.[0]?.paragraphs?.[0]}
      />
      <SiteHeader />
      <main className="flex-1">
        <article className="mx-auto max-w-3xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="overline">Legal</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-[1.1] text-primary mt-4">{doc.title}</h1>
          {doc.updated && (
            <div className="text-sm text-muted-foreground mt-3">Last updated: {doc.updated}</div>
          )}
          <div className="mt-10 space-y-8">
            {doc.sections.map((section, i) => (
              <div key={i}>
                {section.heading && (
                  <h2 className="font-serif-display text-xl text-primary mb-3">{section.heading}</h2>
                )}
                <div className="space-y-4 text-primary/85 leading-relaxed">
                  {section.paragraphs.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              </div>
            ))}
          </div>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * Which design this agency's site is built in decides which of these
 * renders. See lib/siteTemplate.js.
 */
export default function AgencyLegal({ kind = "terms" }) {
  const brand = useBrand();
  if (isEstate(brand)) return <EstateLegal kind={kind} preview={isPreviewing(brand)} />;
  return <AgencyLegalClassic kind={kind} />;
}
